/**
 * Group Structure tab for the Company Profile page (CR-6247).
 *
 * Shows the group this company sits in and its sibling companies, matching the
 * supplier profile's equivalent tab. Siblings are click-through to their own
 * profile, so the group can be walked without going back to Admin Setup.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Network,
  FolderTree,
  MapPin,
  Star,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { coreAPI } from '../../lib/api';
import type { Company } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';

interface GroupStructureTabProps {
  company: Company;
}

/**
 * List the company's group and the companies in it.
 *
 * @param company - the company whose group is shown
 */
export function GroupStructureTab({ company }: GroupStructureTabProps) {
  const navigate = useNavigate();
  const [groupCompanies, setGroupCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const groupId = company.group;
  const groupName = company.group_name;

  const fetchGroupData = useCallback(async () => {
    if (!groupId) {
      setGroupCompanies([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // my_group_members rather than the ?group= list: the default list action
      // is gated to Helix users, so a company admin would get a 403 and an
      // empty tab that reads as "no companies" (CR-4669).
      const res = await coreAPI.getMyGroupMembers({
        company_type: company.company_type,
        group: groupId,
      });
      const companies = res.data.results || [];
      // This company first, then the rest alphabetically.
      companies.sort((a, b) => {
        if (a.id === company.id) return -1;
        if (b.id === company.id) return 1;
        return a.name.localeCompare(b.name);
      });
      setGroupCompanies(companies);
    } catch (err: unknown) {
      console.error('Failed to load group companies', err);
      setGroupCompanies([]);
      setError(getApiErrorMessage(err, 'Failed to load the companies in this group.'));
    } finally {
      setLoading(false);
    }
  }, [groupId, company.id, company.company_type]);

  useEffect(() => {
    void fetchGroupData();
  }, [fetchGroupData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="h-5 w-5" />
          Group Structure
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-start justify-center gap-2 py-8 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        ) : !groupId ? (
          <p className="text-sm text-muted-foreground py-4">
            This company does not belong to any group.
          </p>
        ) : (
          <div className="select-none">
            {/* Root: the group itself */}
            <div className="flex items-center gap-2 py-2 px-2 rounded-md">
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
              <FolderTree className="h-4 w-4 text-purple-500" />
              <div className="flex-1 min-w-0">
                <span className="font-medium truncate">{groupName || 'Group'}</span>
                <div className="text-xs text-muted-foreground">
                  {groupCompanies.length}{' '}
                  {groupCompanies.length === 1 ? 'company' : 'companies'}
                </div>
              </div>
              <Badge
                variant="outline"
                className="text-xs border-purple-300 bg-purple-50 text-purple-700"
              >
                Group
              </Badge>
            </div>

            {groupCompanies.length > 0 ? (
              <div className="border-l border-border ml-4">
                {groupCompanies.map((member) => {
                  const isCurrent = member.id === company.id;
                  const location = member.city
                    ? `${member.city}${member.country ? `, ${member.country}` : ''}`
                    : member.country || 'No location';
                  return (
                    <div
                      key={member.id}
                      className={`flex items-center gap-2 py-2 px-2 rounded-md transition-colors ${
                        isCurrent
                          ? 'bg-primary/5 border border-primary/30'
                          : 'hover:bg-muted/50 cursor-pointer'
                      }`}
                      style={{ paddingLeft: '28px' }}
                      onClick={() => !isCurrent && navigate(`/companies/${member.id}`)}
                    >
                      <span className="w-5" />
                      <MapPin
                        className={`h-4 w-4 ${isCurrent ? 'text-primary' : 'text-blue-500'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate text-sm">{member.name}</span>
                          {isCurrent && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                              <Star className="h-3 w-3" />
                              Current
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">{location}</span>
                      </div>
                      {!member.is_active && (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                      {!isCurrent && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 pl-8">
                No companies in this group.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default GroupStructureTab;
